import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Grid,
  Avatar,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Person,
  Business,
  LocationOn,
  Phone,
  Email,
  Edit,
  Close,
  Save,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import PhotoCropModal from './PhotoCropModal';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../services/firebase';

export default function EditVendorProfile({ open, onClose }) {
  const { userProfile } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    company: '',
    location: '',
    phone: '',
    email: '',
    bio: '',
    memberSince: '',
    profilePhoto: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [vendorData, setVendorData] = useState(null);

  // Photo cropping state
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);

  useEffect(() => {
    if (open && userProfile?.uid) {
      fetchVendorData();
    }
  }, [open, userProfile]);

  const fetchVendorData = async () => {
    try {
      console.log('Fetching vendor data for UID:', userProfile.uid);
      let response;
      try {
        // First try with the user's actual UID
        response = await api.vendors.getById(userProfile.uid);
      } catch (error) {
        console.log('User UID not found, trying fallback...');
        // If that fails, try with a default vendor ID for demo purposes
        response = await api.vendors.getById('vendor_001');
      }

      const data = response.data;
      console.log('Vendor data loaded:', data);
      setVendorData(data);
      setFormData({
        name: data.name || '',
        title: data.title || '',
        company: data.company || '',
        location: data.location || '',
        phone: data.phone || '',
        email: data.email || '',
        bio: data.bio || '',
        memberSince: data.memberSince || '',
        profilePhoto: data.profilePhoto || '',
      });
    } catch (error) {
      console.error('Error fetching wholesaler data:', error);
      // As a last resort, set some default data
      setVendorData({
        name: userProfile?.displayName || 'Wholesaler',
        title: 'Property Wholesaler',
        company: 'Your Company',
        location: 'Your Location',
        phone: '',
        email: userProfile?.email || '',
        bio: '',
        memberSince: 'Recently',
      });
      setFormData({
        name: userProfile?.displayName || 'Wholesaler',
        title: 'Property Wholesaler',
        company: 'Your Company',
        location: 'Your Location',
        phone: '',
        email: userProfile?.email || '',
        bio: '',
        memberSince: 'Recently',
        profilePhoto: '',
      });
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  // Handle photo file selection
  const handlePhotoSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result);
        setCropModalOpen(true);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle cropped photo
  const handlePhotoCrop = async (croppedImageBlob) => {
    try {
      // Enforce same limit as Storage rules (5MB).
      const MAX_PROFILE_PHOTO_BYTES = 5 * 1024 * 1024;
      if (croppedImageBlob?.size > MAX_PROFILE_PHOTO_BYTES) {
        alert('Profile photo must be 5MB or less. Please crop smaller or choose a smaller image.');
        return;
      }

      console.log('Starting photo upload process...');
      console.log('Cropped image blob:', croppedImageBlob);
      console.log('Blob size:', croppedImageBlob.size, 'bytes');

      // Upload to Firebase Storage
      const fileName = `profile-photos/${userProfile.uid}/${Date.now()}.jpg`;
      console.log('Uploading to path:', fileName);

      const storageRef = ref(storage, fileName);
      const snapshot = await uploadBytes(storageRef, croppedImageBlob);
      console.log('Upload completed:', snapshot.metadata);

      const downloadURL = await getDownloadURL(storageRef);
      console.log('Download URL obtained:', downloadURL);

      // Update form data with new photo URL
      setFormData(prev => {
        const newFormData = {
          ...prev,
          profilePhoto: downloadURL
        };
        console.log('Form data updated:', newFormData);
        return newFormData;
      });

      setImageSrc(null);
      console.log('Photo upload process completed successfully');
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert('Failed to upload photo. Please try again.');
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.company.trim()) newErrors.company = 'Company is required';
    if (!formData.location.trim()) newErrors.location = 'Location is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      console.log('Saving vendor profile...');
      console.log('userProfile:', userProfile);
      console.log('userProfile.uid:', userProfile?.uid);
      console.log('vendorData:', vendorData);
      console.log('formData:', formData);

      const updatedData = {
        ...vendorData,
        ...formData,
        updatedAt: new Date().toISOString(),
      };

      console.log('Final updatedData:', updatedData);

      // In a real app, this would call the API to update the profile
      const result = await api.vendors.update(userProfile.uid, updatedData);
      console.log('Update result:', result);

      console.log('Profile updated successfully!');
      onClose();
    } catch (error) {
      console.error('Error updating profile:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      alert('Failed to save profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      title: '',
      company: '',
      location: '',
      phone: '',
      email: '',
      bio: '',
      memberSince: '',
      profilePhoto: '',
    });
    setErrors({});
    setCropModalOpen(false);
    setImageSrc(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ color: '#008080', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Edit sx={{ mr: 1 }} />
          Edit Profile
        </Box>
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {/* Profile Header */}
          <Box sx={{ textAlign: 'center', mb: 4, p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
            <Typography variant="h4" sx={{ color: '#008080', fontWeight: 600, mb: 1 }}>
              {formData.name || 'Your Name'}
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 0.5 }}>
              {formData.title || 'Your Title'}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {formData.company || 'Your Company'}
            </Typography>
          </Box>

          {/* Profile Photo Section */}
          <Typography variant="h6" gutterBottom sx={{ color: '#008080', display: 'flex', alignItems: 'center' }}>
            <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
            Profile Photo
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  src={formData.profilePhoto || undefined}
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: '#008080'
                  }}
                >
                  <Person sx={{ fontSize: 40 }} />
                </Avatar>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Current Photo
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Upload a new photo to update your profile
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" gutterBottom>
                Upload & Crop New Photo
              </Typography>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                sx={{ mb: 1 }}
              >
                Choose Photo
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handlePhotoSelect}
                />
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Supported formats: JPG, PNG, GIF. Max size: 5MB
              </Typography>
            </Grid>
          </Grid>

          {/* Basic Information */}
          <Typography variant="h6" gutterBottom sx={{ color: '#008080', mt: 4, display: 'flex', alignItems: 'center' }}>
            <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
            Basic Information
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Name *"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                error={!!errors.name}
                helperText={errors.name}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Title *"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                error={!!errors.title}
                helperText={errors.title}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Company *"
                value={formData.company}
                onChange={(e) => handleInputChange('company', e.target.value)}
                error={!!errors.company}
                helperText={errors.company}
                required
              />
            </Grid>
          </Grid>

          {/* Contact Information */}
          <Typography variant="h6" gutterBottom sx={{ color: '#008080', mt: 4, display: 'flex', alignItems: 'center' }}>
            <Phone sx={{ mr: 1, verticalAlign: 'middle' }} />
            Contact Information
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone *"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                error={!!errors.phone}
                helperText={errors.phone}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email *"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                error={!!errors.email}
                helperText={errors.email}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Location *"
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                error={!!errors.location}
                helperText={errors.location}
                required
              />
            </Grid>
          </Grid>

          {/* Professional Information */}
          <Typography variant="h6" gutterBottom sx={{ color: '#008080', mt: 4, display: 'flex', alignItems: 'center' }}>
            <Business sx={{ mr: 1, verticalAlign: 'middle' }} />
            Professional Information
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Professional Bio"
                value={formData.bio}
                onChange={(e) => handleInputChange('bio', e.target.value)}
                error={!!errors.bio}
                helperText={errors.bio}
                placeholder="Tell potential investors about your experience and expertise..."
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button
          onClick={handleClose}
          sx={{ color: '#666' }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={<Save />}
          sx={{
            backgroundColor: '#008080',
            '&:hover': { backgroundColor: '#005252' },
            minWidth: 120
          }}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>

      {/* Photo Crop Modal */}
      <PhotoCropModal
        open={cropModalOpen}
        onClose={() => setCropModalOpen(false)}
        imageSrc={imageSrc}
        onCropComplete={handlePhotoCrop}
        aspect={1}
      />
    </Dialog>
  );
}
